import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MeetingProvider } from './context/MeetingContext';
import { PresenciaProvider } from './context/PresenciaContext';
import './index.css';

// Layouts
import AdminLayout from './layouts/AdminLayout';

// Auth pages
import Login    from './pages/auth/Login';
import Register        from './pages/auth/Register';
import ChangePassword      from './pages/auth/ChangePassword';
import NoSubscriptionPage  from './pages/NoSubscriptionPage';

// Admin pages
import Dashboard     from './pages/admin/dashboard/Dashboard';
import Assemblies    from './pages/admin/asambleas/Assemblies';
import AssemblyForm  from './pages/admin/asambleas/AssemblyForm';
import Properties    from './pages/admin/propiedades/Properties';
import PropertyForm  from './pages/admin/propiedades/PropertyForm';
import Users         from './pages/admin/usuarios/Users';
import Guests        from './pages/admin/invitados/Guests';
import Questions     from './pages/admin/preguntas/Questions';
import Companies     from './pages/admin/companias/Companies';
import Events        from './pages/admin/eventos/Events';
import Attendances   from './pages/admin/asistentes/Attendances';
import Buildings     from './pages/admin/torres/Buildings';
import Owners        from './pages/admin/propietarios/Owners';
import Units         from './pages/admin/unidades/Units';
import PropertyAdmins from './pages/admin/administradores/PropertyAdmins';
import Memberships   from './pages/admin/membresias/Memberships';
import ActasReportes  from './pages/admin/reportes/ActasReportes';
import ImportReportes from './pages/admin/reportes/ImportReportes';
import CuentasPropietarios from './pages/admin/reportes/CuentasPropietarios';
import EventReports  from './pages/admin/reportes/EventReports';
import Comunicados   from './pages/admin/comunicados/Comunicados';
import Glosario      from './pages/admin/glosario/Glosario';
import SubscriptionGuard from './components/SubscriptionGuard';
import Profile       from './pages/admin/perfil/Profile';

// Public pages
import ResultsPage from './pages/ResultsPage';
import NotFound from './pages/NotFound';
import MeetingFrameLeft from './pages/MeetingFrameLeft';

// Carga perezosa: MeetingFrame importa @zoom/meetingsdk, cuyo módulo pinta
// un overlay fijo de pantalla completa (#zmmtg-root) apenas se evalúa — si
// se importara arriba como los demás, ese overlay taparía TODA la app
// (incluido /login) desde el primer render, no solo dentro del iframe de
// ZoomFloat donde debe vivir.
const MeetingFrame = lazy(() => import('./pages/MeetingFrame'));

// ── Guards ────────────────────────────────────────────────────────────────────
const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false, assemblyStaffOnly = false }) => {
  const { user, loading, subscriptionState } = useAuth();
  const location = useLocation();
  if (loading) return <div className="loading"><div className="spinner" /><p>Cargando...</p></div>;
  if (!user)   return <Navigate to="/login" replace />;

  // Cambio de contraseña obligatorio — se exige en TODAS las rutas, no solo
  // justo después del login, para que no baste con navegar a otra URL para saltárselo.
  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Admin con membresía completamente vencida (más de 14 días) → bloqueo total
  if (subscriptionState === 'blocked') return <NoSubscriptionPage />;

  if (superAdminOnly  && user.role !== 'super_admin')
    return <Navigate to="/dashboard" replace />;
  if (adminOnly && !['admin', 'super_admin'].includes(user.role))
    return <Navigate to="/dashboard" replace />;
  if (assemblyStaffOnly && !['admin', 'super_admin', 'assembly_admin'].includes(user.role))
    return <Navigate to="/dashboard" replace />;

  return children;
};

const GuestRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (user)    return <Navigate to="/dashboard" replace />;
  return children;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* #20: la ventana de Zoom vive aquí, por encima de las rutas, para que
            no se cierre al cambiar de sección. Ver context/MeetingContext.jsx. */}
        <MeetingProvider>
        {/* F-26: la presencia en la asamblea también vive por encima de las rutas:
            navegar dentro de la app no saca a la persona del cuórum. */}
        <PresenciaProvider>
        <Routes>
          {/* Public */}
          <Route path="/"        element={<Navigate to="/login" />} />
          <Route path="/login"   element={<GuestRoute><Login /></GuestRoute>} />
          <Route path="/register"        element={<GuestRoute><Register /></GuestRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          {/* F-16: /questions y /vote eran pantallas de voto paralelas al panel del
              propietario que se saltaban "Participar" (consentimiento + asistencia).
              Se retiraron: se vota solo desde el panel. Links viejos → al panel. */}
          <Route path="/questions"           element={<Navigate to="/dashboard" replace />} />
          <Route path="/vote/:assemblyId"    element={<Navigate to="/dashboard" replace />} />
          <Route path="/results/:assemblyId" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />

          {/* Zoom Meeting SDK embebido — corren SOLO dentro del iframe de ZoomFloat,
              nunca como página completa. authenticate en el backend valida el token
              de localStorage, que es del mismo origen que el resto de la app. */}
          <Route path="/meeting-frame/left"           element={<MeetingFrameLeft />} />
          <Route path="/meeting-frame/:assemblyId"    element={
            <ProtectedRoute>
              <Suspense fallback={null}>
                <MeetingFrame />
              </Suspense>
            </ProtectedRoute>
          } />

          {/* Dashboard — todos los autenticados */}
          <Route path="/dashboard" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
          </Route>

          {/* Solo super_admin */}
          <Route path="/admin" element={<ProtectedRoute superAdminOnly><AdminLayout /></ProtectedRoute>}>
            <Route path="administradores" element={<PropertyAdmins />} />
            <Route path="membresias"      element={<Memberships />} />
            <Route path="usuarios"        element={<Users />} />
            <Route path="invitados"       element={<Guests />} />
            <Route path="eventos"         element={<Events />} />
            <Route path="companias"       element={<Companies />} />
            <Route path="reportes/eventos" element={<EventReports />} />
          </Route>

          {/* admin + super_admin */}
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
            <Route path="propiedades"           element={<SubscriptionGuard><Properties /></SubscriptionGuard>} />
            <Route path="propiedades/create"    element={<PropertyForm />} />
            <Route path="propiedades/:id/edit"  element={<PropertyForm />} />
            <Route path="asambleas"             element={<SubscriptionGuard><Assemblies /></SubscriptionGuard>} />
            <Route path="asambleas/create"      element={<SubscriptionGuard><AssemblyForm /></SubscriptionGuard>} />
            <Route path="asambleas/:id/edit"    element={<SubscriptionGuard><AssemblyForm /></SubscriptionGuard>} />
            <Route path="torres"                element={<SubscriptionGuard><Buildings /></SubscriptionGuard>} />
            <Route path="propietarios-import"   element={<SubscriptionGuard><Owners /></SubscriptionGuard>} />
            <Route path="reportes/actas"          element={<SubscriptionGuard><ActasReportes /></SubscriptionGuard>} />
            <Route path="reportes/importaciones"  element={<SubscriptionGuard><ImportReportes /></SubscriptionGuard>} />
            <Route path="reportes/cuentas"        element={<SubscriptionGuard><CuentasPropietarios /></SubscriptionGuard>} />
          </Route>

          {/* assembly_admin + admin + super_admin */}
          <Route path="/admin" element={<ProtectedRoute assemblyStaffOnly><AdminLayout /></ProtectedRoute>}>
            <Route path="preguntas"   element={<SubscriptionGuard><Questions /></SubscriptionGuard>} />
            <Route path="asistentes"  element={<SubscriptionGuard><Attendances /></SubscriptionGuard>} />
            <Route path="comunicados" element={<SubscriptionGuard><Comunicados /></SubscriptionGuard>} />
            <Route path="glosario"    element={<Glosario />} />
          </Route>

          {/* Todos los autenticados */}
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route path="unidades" element={<Units />} />
            <Route path="perfil"   element={<Profile />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
        </PresenciaProvider>
        </MeetingProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}